// app/(tabs)/market/reglages.tsx
//
// Écran Réglages. Sections visibles par TOUS les utilisateurs :
//   - Boutiques abonnées (branché sur useSubscriptions)
//   - Favoris (branché sur useFavorites)
// Section visible UNIQUEMENT si role === 'userpro' (via useMarketAuth) :
//   - Mes boutiques (branché sur useMyShops) — un tap sur une boutique
//     navigue vers l'écran dédié app/(tabs)/market/gerer/[shopId].tsx,
//     où vivent l'apparence (ColorPickerField, etc.) et les produits de
//     CETTE boutique précise. Ce fichier-ci ne fait que lister/naviguer.
//     Le bouton "Créer une nouvelle boutique" navigue vers l'écran dédié
//     app/(tabs)/market/creer-boutique.tsx (push classique), qui gère
//     seul son propre formulaire (nom uniquement — le slug et le
//     timezone sont gérés côté useCreateShop/backend, voir
//     hooks/market/useCreateShop.ts et backend/handleMarket.ts →
//     createShop) puis navigue directement vers l'écran de gestion de
//     la boutique fraîchement créée.
//
// PATCH i18n : tous les textes affichés à l'utilisateur passent par
// react-i18next (namespace market.reglages / market.common). Les statuts
// de boutique (Publiée/Brouillon/Désactivée/Archivée) réutilisent
// market.common.status, partagé avec gerer/[shopId].tsx.

import React, { useState, useRef, useCallback } from 'react';
import {
  StyleSheet,
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Platform,
  Animated,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';

import { useScrollAutoHide } from '@/hooks/useScrollAutoHide';
import { useTabBarVisibility } from '@/components/CustomTabBar';
import { useMarketAuth } from '@/hooks/market/useMarketAuth';
import { useSubscriptions } from '@/hooks/market/useSubscriptions';
import { useFavorites } from '@/hooks/market/useFavorites';
import { useMyShops } from '@/hooks/market/useMyShops';

const NATIVE = Platform.OS !== 'web';

export default function MarketSettingsScreen() {
  const router = useRouter();
  const { t } = useTranslation();

  const [headerVisible, setHeaderVisible] = useState(true);
  const [lastTap, setLastTap] = useState<number | null>(null);
  const headerAnim = useRef(new Animated.Value(0)).current;

  const { setTabBarHidden } = useTabBarVisibility();
  const { isUserpro, loading: authLoading } = useMarketAuth();

  const {
    subscribedShops,
    loading: subsLoading,
    loadingMore: subsLoadingMore,
    error: subsError,
    hasMore: subsHasMore,
    loadMore: subsLoadMore,
  } = useSubscriptions();

  const {
    favorites,
    loading: favLoading,
    loadingMore: favLoadingMore,
    error: favError,
    hasMore: favHasMore,
    loadMore: favLoadMore,
  } = useFavorites();

  // N'appelle useMyShops que pour un userpro — évite un appel réseau
  // inutile pour tous les autres utilisateurs qui ne verront jamais
  // cette section.
  const {
    shops: myShops,
    loading: myShopsLoading,
    error: myShopsError,
  } = useMyShops();

  // Helper : traduit un statut de boutique via market.common.status,
  // en tombant sur la variante "publiée/archivée" (pas "Product").
  const shopStatusLabel = useCallback((status: string) => {
    switch (status) {
      case 'published': return t('market.common.status.published');
      case 'draft':      return t('market.common.status.draft');
      case 'disabled':   return t('market.common.status.disabled');
      case 'archived':   return t('market.common.status.archived');
      default:           return status;
    }
  }, [t]);

  const showHeader = useCallback(() => {
    setHeaderVisible(prevVisible => {
      if (!prevVisible) {
        Animated.spring(headerAnim, {
          toValue: 0,
          useNativeDriver: true,
          tension: 80,
          friction: 10,
        }).start();
      }
      return true;
    });
  }, [headerAnim]);

  const hideHeader = useCallback(() => {
    setHeaderVisible(prevVisible => {
      if (prevVisible) {
        Animated.spring(headerAnim, {
          toValue: -100,
          useNativeDriver: true,
          tension: 80,
          friction: 10,
        }).start();
      }
      return false;
    });
  }, [headerAnim]);

  const { handleScroll } = useScrollAutoHide({
    onShow: useCallback(() => {
      showHeader();
      setTabBarHidden(false);
    }, [showHeader, setTabBarHidden]),
    onHide: useCallback(() => {
      hideHeader();
      setTabBarHidden(true);
    }, [hideHeader, setTabBarHidden]),
    onScrollExtra: useCallback((e: any) => {
      const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
      const ratio = (contentOffset.y + layoutMeasurement.height) / contentSize.height;
      if (ratio > 0.85) {
        if (subsHasMore && !subsLoadingMore) subsLoadMore();
        if (favHasMore && !favLoadingMore) favLoadMore();
      }
    }, [subsHasMore, subsLoadingMore, subsLoadMore, favHasMore, favLoadingMore, favLoadMore]),
  });

  const toggleHeader = useCallback(() => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    if (headerVisible) {
      hideHeader();
      setTabBarHidden(true);
    } else {
      showHeader();
      setTabBarHidden(false);
    }
  }, [headerVisible, hideHeader, showHeader, setTabBarHidden]);

  const handleDoubleTap = useCallback(() => {
    const now = Date.now();
    if (lastTap && now - lastTap < 300) toggleHeader();
    else setLastTap(now);
  }, [lastTap, toggleHeader]);

  const handleShopPress = useCallback((slug: string) => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    router.push(`/(tabs)/market/boutiques/${slug}`);
  }, [router]);

  const handleProductPress = useCallback((productId: string) => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    router.push(`/(tabs)/market/produits/${productId}`);
  }, [router]);

  // Tap sur une de "mes boutiques" → écran de gestion dédié à CETTE
  // boutique (apparence + produits), pas boutiques/[slug].tsx (qui est
  // la vue PUBLIQUE, pas l'espace d'édition).
  const handleManageShopPress = useCallback((shopId: string) => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    router.push(`/(tabs)/market/gerer/${shopId}`);
  }, [router]);

  // Navigue vers l'écran dédié de création de boutique (push classique).
  const handleGoToCreateShop = useCallback(() => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    router.push('/(tabs)/market/creer-boutique');
  }, [router]);

  return (
    <View style={styles.container}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        onTouchEnd={handleDoubleTap}
      >
        <View style={styles.headerBlock}>
          <Ionicons name="settings-outline" size={32} color="#6610B1" />
          <Text style={styles.headerTitle}>{t('market.reglages.headerTitle')}</Text>
          <Text style={styles.headerSub}>
            {isUserpro ? t('market.reglages.headerSubPro') : t('market.reglages.headerSubStandard')}
          </Text>
        </View>

        {/* ══════════════════════════════════════════════════════════
            SECTIONS VISIBLES PAR TOUS LES UTILISATEURS
            ══════════════════════════════════════════════════════════ */}

        {/* ── Boutiques abonnées ── */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('market.reglages.sections.subscribedShops')}</Text>
          <View style={styles.sectionCard}>
            {subsLoading && (
              <View style={styles.centerBox}>
                <ActivityIndicator size="small" color="#6610B1" />
              </View>
            )}

            {!subsLoading && subsError && (
              <View style={styles.centerBox}>
                <Text style={styles.centerBoxText}>
                  {subsError === 'session_expired' ? t('market.common.sessionExpired') : t('market.common.cannotLoad')}
                </Text>
              </View>
            )}

            {!subsLoading && !subsError && subscribedShops.length === 0 && (
              <View style={styles.centerBox}>
                <Text style={styles.centerBoxText}>{t('market.reglages.noSubscribedShops')}</Text>
              </View>
            )}

            {!subsLoading && subscribedShops.map((shop, index) => (
              <TouchableOpacity
                key={shop.shop_id}
                style={[styles.shopRow, index === subscribedShops.length - 1 && styles.rowLast]}
                activeOpacity={0.7}
                onPress={() => handleShopPress(shop.slug)}
              >
                <View style={styles.shopRowIconWrap}>
                  <Ionicons name="storefront" size={20} color="#6610B1" />
                </View>
                <View style={{ flex: 1 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Text style={styles.shopRowName} numberOfLines={1}>{shop.name}</Text>
                    {shop.is_verified && (
                      <Ionicons name="checkmark-circle" size={13} color="#6610B1" style={{ marginLeft: 4 }} />
                    )}
                  </View>
                  <Text style={styles.shopRowMeta}>
                    {t('market.reglages.productsCount', { count: shop.total_products })}
                  </Text>
                </View>
                <Ionicons name="chevron-forward" size={18} color="#C4B5FD" />
              </TouchableOpacity>
            ))}

            {subsLoadingMore && (
              <View style={styles.centerBox}>
                <ActivityIndicator size="small" color="#6610B1" />
              </View>
            )}
          </View>
        </View>

        {/* ── Favoris ── */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('market.reglages.sections.favorites')}</Text>
          <View style={styles.sectionCard}>
            {favLoading && (
              <View style={styles.centerBox}>
                <ActivityIndicator size="small" color="#6610B1" />
              </View>
            )}

            {!favLoading && favError && (
              <View style={styles.centerBox}>
                <Text style={styles.centerBoxText}>
                  {favError === 'session_expired' ? t('market.common.sessionExpired') : t('market.common.cannotLoad')}
                </Text>
              </View>
            )}

            {!favLoading && !favError && favorites.length === 0 && (
              <View style={styles.centerBox}>
                <Text style={styles.centerBoxText}>{t('market.reglages.noFavorites')}</Text>
              </View>
            )}

            {!favLoading && favorites.map((product, index) => (
              <TouchableOpacity
                key={product.product_id}
                style={[styles.shopRow, index === favorites.length - 1 && styles.rowLast]}
                activeOpacity={0.7}
                onPress={() => handleProductPress(product.product_id)}
              >
                <View style={styles.shopRowIconWrap}>
                  <Ionicons name="image-outline" size={18} color="#6610B1" />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={styles.shopRowName} numberOfLines={1}>{product.name}</Text>
                  <Text style={styles.shopRowMeta} numberOfLines={1}>{product.shop_name}</Text>
                </View>
                <Ionicons name="chevron-forward" size={18} color="#C4B5FD" />
              </TouchableOpacity>
            ))}

            {favLoadingMore && (
              <View style={styles.centerBox}>
                <ActivityIndicator size="small" color="#6610B1" />
              </View>
            )}
          </View>
        </View>

        {/* ══════════════════════════════════════════════════════════
            SECTIONS RÉSERVÉES AUX USERPRO
            ══════════════════════════════════════════════════════════ */}

        {!authLoading && isUserpro && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{t('market.reglages.sections.myShops')}</Text>
            <View style={styles.sectionCard}>
              {myShopsLoading && (
                <View style={styles.centerBox}>
                  <ActivityIndicator size="small" color="#6610B1" />
                </View>
              )}

              {!myShopsLoading && myShopsError && (
                <View style={styles.centerBox}>
                  <Text style={styles.centerBoxText}>
                    {myShopsError === 'session_expired' ? t('market.common.sessionExpired') : t('market.common.cannotLoad')}
                  </Text>
                </View>
              )}

              {!myShopsLoading && !myShopsError && myShops.length === 0 && (
                <View style={styles.centerBox}>
                  <Text style={styles.centerBoxText}>{t('market.reglages.noMyShops')}</Text>
                </View>
              )}

              {!myShopsLoading && myShops.map((shop) => (
                <TouchableOpacity
                  key={shop.id}
                  style={styles.shopRow}
                  activeOpacity={0.7}
                  onPress={() => handleManageShopPress(shop.id)}
                >
                  <View style={styles.shopRowIconWrap}>
                    <Ionicons name="storefront" size={20} color="#6610B1" />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.shopRowName}>{shop.name}</Text>
                    <Text style={styles.shopRowMeta}>
                      {shopStatusLabel(shop.status)}
                    </Text>
                  </View>
                  <View style={[
                    styles.statusPill,
                    shop.status === 'published' ? styles.statusPillPublished : styles.statusPillDraft,
                  ]}>
                    <Text style={[
                      styles.statusPillText,
                      shop.status === 'published' ? styles.statusPillTextPublished : styles.statusPillTextDraft,
                    ]}>
                      {shopStatusLabel(shop.status)}
                    </Text>
                  </View>
                  <Ionicons name="chevron-forward" size={18} color="#C4B5FD" style={{ marginLeft: 6 }} />
                </TouchableOpacity>
              ))}

              {/* ── Créer une nouvelle boutique ──
                  Navigue vers l'écran dédié creer-boutique.tsx (push
                  classique). Ce fichier-ci ne gère plus aucun état de
                  formulaire de création. */}
              <TouchableOpacity
                style={styles.createShopBtn}
                activeOpacity={0.8}
                onPress={handleGoToCreateShop}
              >
                <Ionicons name="add-circle" size={20} color="#6610B1" />
                <Text style={styles.createShopBtnText}>{t('market.reglages.createShopButton')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        <View style={{ height: 40 }} />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F0F2F5',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 14,
    paddingTop: 20,
  },
  headerBlock: {
    alignItems: 'center',
    paddingVertical: 20,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginTop: 8,
  },
  headerSub: {
    fontSize: 13,
    color: '#999',
    marginTop: 4,
    textAlign: 'center',
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: '700',
    color: '#999',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 8,
    marginLeft: 4,
  },
  sectionCard: {
    backgroundColor: '#fff',
    borderRadius: 14,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 6,
    elevation: 2,
  },
  centerBox: {
    alignItems: 'center',
    paddingVertical: 20,
  },
  centerBoxText: {
    fontSize: 13,
    color: '#999',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 13,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  rowLast: {
    borderBottomWidth: 0,
  },
  rowIconWrap: {
    width: 34,
    height: 34,
    borderRadius: 10,
    backgroundColor: '#F3E8FF',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 10,
  },
  rowLabel: {
    flex: 1,
    fontSize: 14,
    color: '#333',
    fontWeight: '500',
  },
  rowSub: {
    fontSize: 13,
    color: '#999',
    marginRight: 8,
  },
  shopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 13,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  shopRowIconWrap: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: '#F3E8FF',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  shopRowName: {
    fontSize: 15,
    fontWeight: '700',
    color: '#222',
  },
  shopRowMeta: {
    fontSize: 12,
    color: '#999',
    marginTop: 2,
  },
  statusPill: {
    paddingHorizontal: 9,
    paddingVertical: 4,
    borderRadius: 10,
  },
  statusPillPublished: {
    backgroundColor: '#EDE4FF',
  },
  statusPillDraft: {
    backgroundColor: '#F3F4F6',
  },
  statusPillText: {
    fontSize: 11,
    fontWeight: '700',
  },
  statusPillTextPublished: {
    color: '#6610B1',
  },
  statusPillTextDraft: {
    color: '#6B7280',
  },
  createShopBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    backgroundColor: '#F9F5FF',
  },
  createShopBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6610B1',
  },
});
